import { API_BASE, requestJson } from "../utils";
import { validateJob } from "../api/jobs";

const TERMINAL_IMPORT_STATUSES = new Set(["completed", "stopped", "failed"]);

export function buildCollectionImportUrl(username) {
  return `${API_BASE}/${encodeURIComponent(username)}/`;
}

export async function requestCollectionImport(
  username,
  { method = "GET", signal, fetcher } = {},
) {
  const response = await requestJson(
    buildCollectionImportUrl(username),
    {
      method,
      credentials: "include",
      ...(signal ? { signal } : {}),
    },
    fetcher,
  );
  return { statusCode: response.status, data: validateJob(response.data) };
}

/**
 * @typedef {Object} TerminalCollectionImport
 * @property {"completed" | "failed" | "stopped"} status
 * @property {number} job_id
 * @property {number} import_total
 * @property {string | null} acknowledged_at
 */
export function isUnacknowledgedImport(job) {
  return (
    TERMINAL_IMPORT_STATUSES.has(job?.status) && job.acknowledged_at === null
  );
}

/** @returns {Promise<TerminalCollectionImport>} */
export async function acknowledgeCollectionImport(
  username,
  jobId,
  { signal, fetcher } = {},
) {
  const response = await requestJson(
    `${buildCollectionImportUrl(username)}imports/${jobId}/acknowledge/`,
    { method: "POST", ...(signal ? { signal } : {}) },
    fetcher,
  );
  const job = validateJob(response.data);
  if (
    response.status !== 200 ||
    job.job_id !== jobId ||
    !TERMINAL_IMPORT_STATUSES.has(job.status) ||
    typeof job.acknowledged_at !== "string"
  ) {
    throw new Error(
      "Invalid import acknowledgement response. Retry dismissal.",
    );
  }
  return job;
}

export function resolveCollectionImportResponse({ statusCode, data }) {
  const status = data && typeof data === "object" ? data : {};
  const jobStatus = status.status;

  if (jobStatus === "running" || jobStatus === "started") {
    return { kind: "running", status };
  }

  if (jobStatus === "stopping") {
    return { kind: "stopping", status };
  }

  if (jobStatus === "completed") {
    return { kind: "completed", status };
  }

  if (jobStatus === "stopped") {
    return { kind: "stopped", status };
  }

  if (jobStatus === "failed") {
    return { kind: "failed", status };
  }

  if (jobStatus === "none") return { kind: "none", status };

  return { kind: "unknown", status };
}

export function isCollectionImportActive(status) {
  return Boolean(
    status &&
      typeof status === "object" &&
      !TERMINAL_IMPORT_STATUSES.has(status.status) &&
      ["started", "running", "stopping"].includes(status.status),
  );
}

export function getCollectionImportProgress(status) {
  const total = status?.import_total;
  const known = status && status.progress != null && total > 0;

  return {
    known: Boolean(known),
    current: known ? status.progress : null,
    total: known ? total : null,
    value: known && total ? status.progress / total : 0,
  };
}

export function getCollectionImportPresentation(status, starting = false) {
  const progress = getCollectionImportProgress(status);
  return {
    label: starting
      ? "Starting import…"
      : status?.status === "stopping"
        ? "Stopping import…"
        : isCollectionImportActive(status)
          ? "Importing collection…"
          : "Import collection",
    detail:
      !starting && progress.known
        ? `${progress.current} of ${progress.total} releases`
        : "",
    value: !starting && progress.known ? progress.value : null,
  };
}
